import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth, hasRole } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import StatusBadge from "@/components/StatusBadge";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, AlertTriangle } from "lucide-react";

const ROLE_OPTS = [
  { value: "admin", label: "Admin" },
  { value: "subadmin", label: "Sub-Admin (Read-only)" },
  { value: "sales", label: "Sales BDM" },
  { value: "operations", label: "Operations" },
  { value: "approval", label: "Approval Team" },
  { value: "accountant", label: "Accountant" },
  { value: "backend_executive", label: "Backend Executive" },
  { value: "market_developer", label: "Market Developer" },
  { value: "ibo", label: "IBO" },
  // NOTE: `distributor` role is intentionally omitted here — distributor logins
  // are created on the Distributors page so the login is always tied to a
  // distributor record.
];

const ROLE_LABEL = Object.fromEntries(ROLE_OPTS.map((r) => [r.value, r.label]));

// Client-side validation to mirror backend rules:
//  - distributor is exclusive
//  - ibo can be STANDALONE or combined only with `sales` (Sales BDM)
function validateRoles(roles) {
  if (!roles || roles.length === 0) return "Please select at least one role";
  const s = new Set(roles);
  if (s.has("distributor") && s.size > 1)
    return "Distributor role cannot be combined with any other role";
  if (s.has("ibo")) {
    if (![...s].every((r) => r === "ibo" || r === "sales"))
      return "IBO can be standalone or combined only with Sales BDM (no other roles)";
  }
  return null;
}

const empty = {
  name: "", email: "", password: "", roles: ["sales"], status: "active",
  assigned_subadmin_id: "",
};

const rolesOf = (u) => (Array.isArray(u.roles) && u.roles.length ? u.roles : (u.role ? [u.role] : []));

export default function Users() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const canEdit = hasRole(user, "admin");
  const salesOnly = hasRole(user, "subadmin");
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState("");

  const load = useCallback(() => api.get("/users").then(({ data }) => setUsers(data)), []);
  useEffect(() => { load(); }, [load]);

  const subadmins = users.filter((u) => rolesOf(u).includes("subadmin"));
  const subadminMap = Object.fromEntries(subadmins.map((s) => [s.id, s.name]));

  const openNew = () => { setForm(empty); setEditing(null); setOpen(true); };
  const openEdit = (u) => {
    const uroles = rolesOf(u);
    setForm({
      ...u,
      roles: uroles.length ? uroles : ["sales"],
      password: "",
      assigned_subadmin_id: u.assigned_subadmin_id || "",
    });
    setEditing(u.id);
    setOpen(true);
  };

  const toggleRole = (role) => {
    setForm((f) => {
      const cur = new Set(f.roles || []);
      if (cur.has(role)) cur.delete(role);
      else cur.add(role);
      return { ...f, roles: Array.from(cur) };
    });
  };

  const rolesError = validateRoles(form.roles);
  const hasRoleInForm = (r) => (form.roles || []).includes(r);

  const save = async () => {
    const err = validateRoles(form.roles);
    if (err) { toast.error(err); return; }
    try {
      const payload = { ...form };
      if (editing && !payload.password) delete payload.password;
      // Only send assigned_subadmin_id when user is a sales BDM
      if (!hasRoleInForm("sales")) delete payload.assigned_subadmin_id;
      // Drop legacy single-role field so backend uses `roles`
      delete payload.role;
      if (editing) {
        await api.put(`/users/${editing}`, payload);
        toast.success("User updated");
      } else {
        await api.post("/users", payload);
        toast.success("User created");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this user?")) return;
    try {
      await api.delete(`/users/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const filtered = users
    .filter((u) => (salesOnly ? rolesOf(u).includes("sales") : true))
    .filter((u) =>
      [u.name, u.email, rolesOf(u).join(" ")].join(" ").toLowerCase().includes(search.toLowerCase())
    );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display font-bold text-2xl tracking-tight">Users</h1>
          <p className="text-sm text-muted-foreground">Manage system users and their roles</p>
        </div>
        <Button onClick={openNew} disabled={!canEdit} className={canEdit ? "" : "hidden"} data-testid="add-user-btn"><Plus className="w-4 h-4 mr-2" /> Add user</Button>
      </div>

      <Card className="p-4">
        <Input
          placeholder="Search users..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="max-w-sm"
          data-testid="users-search"
        />
      </Card>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Status</TableHead>
                {canEdit && <TableHead>Assigned Subadmin</TableHead>}
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 && (
                <TableRow><TableCell colSpan={canEdit ? 6 : 5} className="text-center text-muted-foreground py-8">No users</TableCell></TableRow>
              )}
              {filtered.map((u) => {
                const uroles = rolesOf(u);
                const isSales = uroles.includes("sales");
                return (
                <TableRow
                  key={u.id}
                  data-testid={`user-row-${u.email}`}
                  className={isSales ? "cursor-pointer hover:bg-muted/50" : ""}
                  onClick={isSales ? () => navigate(`/users/${u.id}/orders`) : undefined}
                >
                  <TableCell className="font-medium">{u.name}</TableCell>
                  <TableCell>{u.email}</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {uroles.map((r) => (
                        <span
                          key={r}
                          className="inline-block text-[10px] font-medium px-1.5 py-0.5 rounded bg-muted text-foreground/80"
                          data-testid={`role-tag-${u.email}-${r}`}
                        >
                          {ROLE_LABEL[r] || r}
                        </span>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell><StatusBadge status={u.status} /></TableCell>
                  {canEdit && (
                    <TableCell className="text-xs">
                      {isSales ? (subadminMap[u.assigned_subadmin_id] || <span className="text-muted-foreground">—</span>) : <span className="text-muted-foreground">—</span>}
                    </TableCell>
                  )}
                  <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                    {canEdit ? (
                      <>
                        <Button variant="ghost" size="sm" onClick={() => openEdit(u)} data-testid={`edit-user-${u.email}`}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => del(u.id)} data-testid={`delete-user-${u.email}`}>
                          <Trash2 className="w-4 h-4 text-red-600" />
                        </Button>
                      </>
                    ) : (
                      <span className="text-xs text-muted-foreground">{isSales ? "View →" : "Read-only"}</span>
                    )}
                  </TableCell>
                </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? "Edit user" : "Add user"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-name-input" />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-email-input" />
            </div>
            <div className="space-y-2">
              <Label>Password {editing && <span className="text-xs text-muted-foreground">(leave blank to keep)</span>}</Label>
              <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-password-input" />
            </div>
            <div className="grid grid-cols-1 gap-4">
              <div className="space-y-2">
                <Label>Roles <span className="text-xs text-muted-foreground">(select one or more)</span></Label>
                <div className="grid grid-cols-2 gap-2 p-3 rounded-md border border-input">
                  {ROLE_OPTS.map((r) => (
                    <label
                      key={r.value}
                      className="flex items-center gap-2 text-sm cursor-pointer select-none"
                      data-testid={`role-checkbox-${r.value}`}
                    >
                      <Checkbox
                        checked={hasRoleInForm(r.value)}
                        onCheckedChange={() => toggleRole(r.value)}
                      />
                      <span>{r.label}</span>
                    </label>
                  ))}
                </div>
                {rolesError && (
                  <div className="flex items-start gap-1.5 text-xs text-red-600" data-testid="roles-error">
                    <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                    <span>{rolesError}</span>
                  </div>
                )}
                <p className="text-[11px] text-muted-foreground leading-relaxed">
                  Rules: <b>Distributor</b> is exclusive (cannot combine with any other role).
                  <b> IBO</b> can be standalone <i>or</i> combined only with <b>Sales BDM</b>.
                </p>
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="user-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {hasRoleInForm("sales") && (
              <div className="space-y-2">
                <Label>Assigned Subadmin <span className="text-xs text-muted-foreground">(optional)</span></Label>
                <Select
                  value={form.assigned_subadmin_id || "__none__"}
                  onValueChange={(v) => setForm({ ...form, assigned_subadmin_id: v === "__none__" ? "" : v })}
                >
                  <SelectTrigger data-testid="user-assigned-subadmin-select"><SelectValue placeholder="None" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">None</SelectItem>
                    {subadmins.map((s) => (
                      <SelectItem key={s.id} value={s.id}>{s.name} ({s.email})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={!!rolesError} data-testid="save-user-btn">{editing ? "Update" : "Create"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
